import { useResendVerification, useVerifyEmail } from "@/features/auth/auth.hooks";
import { useResetRequest, useResetVerify } from "@/features/password/password.hooks";
import { usePasswordStore } from "@/features/password/password.store";
import { rootRoute } from "@/routes";
import { cn } from "@/shared/lib/cn";
import { CommonHeader } from "@/shared/ui/composites";
import { Button, Divider, HStack, Spinner, VStack } from "@/shared/ui/primitives";
import { createRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const CODE_LENGTH = 6;
const CODE_TTL_SECONDS = 600;
const verifySearchSchema = z.object({
	email: z.string().email().optional(),
	redirect: z.string().refine((value) => value.startsWith("/"), {
		message: "Redirect must be an internal path",
	}),
	type: z.enum(["reset-password"]).optional(),
});

const Desktop = () => {
	const { t } = useTranslation();
	const { email: searchEmail, redirect, type } = verifyRoute.useSearch();
	const navigate = useNavigate();
	const router = useRouter();

	const isResetPassword = type === "reset-password";

	const verify = useVerifyEmail();
	const resetVerify = useResetVerify();
	const resend = useResendVerification();
	const resetResend = useResetRequest();
	const pendingResetRequest = usePasswordStore((state) => state.pendingResetRequest);
	const setPendingResetRequest = usePasswordStore((state) => state.setPendingResetRequest);
	const email = isResetPassword
		? (searchEmail ?? pendingResetRequest?.email ?? "")
		: (searchEmail ?? "");

	const activeMutation = isResetPassword ? resetVerify : verify;

	const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(""));
	const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
	const [remaining, setRemaining] = useState(CODE_TTL_SECONDS);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!isResetPassword) {
			setPendingResetRequest(null);
		}
	}, [isResetPassword, setPendingResetRequest]);

	useEffect(() => {
		if (isResetPassword && !searchEmail && pendingResetRequest?.email) {
			navigate({
				to: "/verify",
				search: { email: pendingResetRequest.email, redirect, type: "reset-password" },
				replace: true,
			});
		}
	}, [isResetPassword, navigate, pendingResetRequest?.email, redirect, searchEmail]);

	useEffect(() => {
		if (remaining <= 0) return;
		const timer = setTimeout(() => setRemaining((prev) => prev - 1), 1000);
		return () => clearTimeout(timer);
	}, [remaining]);

	const minutes = Math.floor(remaining / 60);
	const seconds = remaining % 60;
	const timeDisplay = `${minutes}:${seconds.toString().padStart(2, "0")}`;

	const handleChange = (index: number, value: string) => {
		if (!/^\d?$/.test(value)) return;
		const next = [...code];
		next[index] = value;
		setCode(next);
		setError("");
		if (value && index < CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
	};

	const handleKeyDown = (index: number, e: KeyboardEvent) => {
		if (e.key === "Backspace" && !code[index] && index > 0) {
			inputsRef.current[index - 1]?.focus();
		}
	};

	const isExpired = remaining <= 0;

	const handleVerify = () => {
		if (!email) {
			setError(t("auth.resetError"));
			return;
		}

		const value = code.join("");
		if (value.length !== CODE_LENGTH) return;

		const onError = () => setError(t("auth.verifyError"));

		if (isResetPassword) {
			resetVerify.mutate(
				{ email, code: value },
				{
					onSuccess: (data) => {
						setPendingResetRequest(null);
						if (data.accessToken && data.refreshToken) {
							navigate({ to: "/" });
						} else {
							navigate({ to: redirect });
						}
					},
					onError,
				},
			);
		} else {
			verify.mutate(
				{ email, code: value },
				{
					onSuccess: (data) => {
						if (data.accessToken && data.refreshToken) {
							navigate({ to: "/" });
						} else {
							navigate({ to: redirect });
						}
					},
					onError,
				},
			);
		}
	};

	const handleResend = () => {
		const onSuccess = () => {
			setRemaining(CODE_TTL_SECONDS);
			setCode(Array(CODE_LENGTH).fill(""));
			setError("");
			inputsRef.current[0]?.focus();
		};

		if (isResetPassword) {
			if (!pendingResetRequest || pendingResetRequest.email !== email) {
				setError(t("auth.resetError"));
				return;
			}

			resetResend.mutate(
				{ email: pendingResetRequest.email, password: pendingResetRequest.password },
				{
					onSuccess,
					onError: () => setError(t("auth.resetError")),
				},
			);
		} else {
			resend.mutate({ email }, { onSuccess, onError: () => setError(t("auth.verifyError")) });
		}
	};

	const handlePaste = (e: React.ClipboardEvent) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
		if (!pasted) return;
		const next = [...code];
		for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
		setCode(next);
		inputsRef.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
	};

	return (
		<VStack className="w-full h-full bg-bg-weak px-[20px] md:px-0">
			<form
				className="flex-col w-full max-w-[480px] rounded-[16px] outline-outline p-[20px] gap-[24px]"
				onSubmit={(e) => {
					e.preventDefault();
					handleVerify();
				}}
			>
				<CommonHeader
					title={t("auth.verify")}
					description={t("auth.verifyDescription", { email })}
					onTap={() => router.history.back()}
				/>
				<Divider className="w-full" />
				<VStack className="w-full">
					<HStack className="w-full justify-between text-sm p-[10px]">
						<span className="text-ct-strong font-medium">{t("auth.verifyNumber")}</span>
						<span className={cn("font-normal", isExpired ? "text-dg-strong" : "text-pm-strong")}>
							{isExpired ? t("auth.verifyExpired") : timeDisplay}
						</span>
					</HStack>
					<HStack className="w-full gap-[6px] pb-[16px]">
						{code.map((digit, i) => (
							<input
								key={i}
								ref={(el) => {
									inputsRef.current[i] = el;
								}}
								type="text"
								inputMode="numeric"
								maxLength={1}
								value={digit}
								placeholder="0"
								onChange={(e) => handleChange(i, e.target.value)}
								onKeyDown={(e) => handleKeyDown(i, e)}
								onPaste={handlePaste}
								className={cn(
									"w-full text-center py-[20px] rounded-[10px] bg-bg-regular text-ct-strong placeholder:text-ct-weak text-md font-normal",
									error && "outline-dg-strong",
								)}
							/>
						))}
					</HStack>
					{error && (
						<span className="w-full px-[10px] py-[6px] text-sm text-dg-strong font-normal">
							{error}
						</span>
					)}
				</VStack>
				{isExpired ? (
					<Button
						size="lg"
						className="w-full"
						onClick={handleResend}
						disabled={resend.isPending || resetResend.isPending}
					>
						{resend.isPending || resetResend.isPending ? (
							<Spinner size="sm" />
						) : (
							t("auth.resendAction")
						)}
					</Button>
				) : (
					<Button
						variant="primary"
						size="lg"
						className="w-full"
						type="submit"
						disabled={activeMutation.isPending}
					>
						{activeMutation.isPending ? <Spinner size="sm" /> : t("auth.verifyAction")}
					</Button>
				)}
			</form>
		</VStack>
	);
};

export const verifyRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/verify",
	component: Desktop,
	validateSearch: verifySearchSchema,
});
